import React, { useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import DateTimePicker from 'components/form/DateTimePicker';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { NOTICE_TYPE_KEY, NoticeType, createAdminNotice } from 'stores/NoticeAdminStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type NoticeCreateFormHandle = {
  submit: () => void;
};

type NoticeCreateFormProps = {
  /** 固定のNoticeType（ページごとに決まる） */
  noticeType: NoticeType;
  /** ユーザー一覧（UserSelect用） */
  users: UserResult[];
  /** 作成完了コールバック */
  onCreate: () => void;
  /** キャンセルコールバック */
  onCancel: () => void;
  /** ローディング制御 */
  setProgress: (v: boolean) => void;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const NoticeCreateForm = React.forwardRef<NoticeCreateFormHandle, NoticeCreateFormProps>(
  ({ noticeType, users, onCreate, onCancel, setProgress }, ref) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [externalLink, setExternalLink] = useState('');
    const [deadline, setDeadline] = useState('');
    const [priority, setPriority] = useState<number>(5);
    const [userIds, setUserIds] = useState<string[]>([]);

    const [errors, setErrors] = useState<Record<string, string>>({});

    const isNoticeType = noticeType === NOTICE_TYPE_KEY.NOTICE;

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!title) newErrors.title = MSG_REQUIRED('タイトル');
      else if (title.length > 300) newErrors.title = MSG_MAX_LENGTH('タイトル', 300);

      if (!content) newErrors.content = MSG_REQUIRED('内容');
      else if (content.length > 10000) newErrors.content = MSG_MAX_LENGTH('内容', 10000);

      if (externalLink && externalLink.length > 500)
        newErrors.externalLink = MSG_MAX_LENGTH('外部リンク', 500);

      if (isNoticeType && userIds.length === 0) {
        newErrors.userIds = '対象ユーザーを1人以上選択してください。';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createAdminNotice({
        noticeType,
        title,
        content,
        ...(externalLink && { externalLink }),
        ...(deadline && { deadline }),
        priority,
        ...(isNoticeType && { userIds }),
      })
        .then(() => {
          successDefault('通知を作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('通知の作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={STICKY_HEADER_SX}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={onCancel}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>
                {NoticeType[noticeType]}の新規作成
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            {isNoticeType
              ? '指定したユーザーに個人向け通知を送信します'
              : '全ユーザーに向けて通知を送信します'}
          </Typography>
        </Box>

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.title}
                helperText={errors.title}
                slotProps={{ htmlInput: { maxLength: 300 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="内容"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                size="small"
                required
                multiline
                rows={6}
                error={!!errors.content}
                helperText={errors.content || `${content.length}/10000`}
                slotProps={{ htmlInput: { maxLength: 10000 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* 配信設定 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              配信設定
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    期限
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <DateTimePicker
                      label=""
                      mode="end"
                      value={deadline}
                      onChange={setDeadline}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    優先度（1: 最高 〜 5: 最低）
                  </Typography>
                  <ToggleButtonGroup
                    value={priority}
                    exclusive
                    onChange={(_, v) => {
                      if (v !== null) setPriority(v);
                    }}
                    size="small"
                    sx={{ mt: 0.5, display: 'flex' }}
                  >
                    {[1, 2, 3, 4, 5].map((v) => (
                      <ToggleButton
                        key={v}
                        value={v}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      >
                        {v}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Stack>
              <TextField
                label="リンク"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                fullWidth
                size="small"
                placeholder="https://..."
                error={!!errors.externalLink}
                helperText={errors.externalLink}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </Box>

          {/* 対象ユーザー（NOTICEのみ） */}
          {isNoticeType && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  対象ユーザー
                </Typography>
                <UserSelect
                  mode="multi"
                  users={users}
                  selectedUserIds={userIds}
                  onChange={setUserIds}
                  label="対象ユーザー"
                  required
                  error={!!errors.userIds}
                  helperText={errors.userIds}
                />
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    );
  },
);

NoticeCreateForm.displayName = 'NoticeCreateForm';

export default NoticeCreateForm;
